import { type Schemas } from "@/types";
import {
  Anchor,
  Badge,
  Box,
  Flex,
  SimpleGrid,
  Stack,
  Text,
} from "@mantine/core";

import classes from "../..//Home.module.css";
import { IconDots, IconPhone } from "@tabler/icons-react";
import { useTimeLeft } from "./useTimeLeft";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { lowerCaseNthLetter } from "@/utils/filters";

interface CallbackItemProps {
  callback: Schemas["PhoneCallRetrieveDto"];
}

export default function CallbackItem({ callback }: CallbackItemProps) {
  const { t } = useTranslation("features");
  const callBackName =
    callback.lead?.fullName ?? callback.contact?.fullName ?? "";
  const startDate = useMemo(() => {
    return callback.startDate ? new Date(callback.startDate) : null;
  }, [callback.startDate]);
  const { timeLeft, isDue } = useTimeLeft(startDate);
  const processStage = callback.lead?.processStage ?? "";
  return (
    <Anchor
      href={`/app/leads/${callback.lead?.id}`}
      target="_blank"
      rel="noopener noreferrer"
      style={{ textDecoration: "none", color: "inherit" }}
    >
      <Box className={classes.listItem}>
        <Flex justify="space-between" align="center" direction="row" mb={4}>
          <Flex justify="flex-start" align="center" direction="row" gap={8}>
            <Text className={classes.listItemTitle}>{callBackName}</Text>
            <Badge leftSection={<IconPhone size={12} />}>
              {callback.lead?.callCount ?? 0}
            </Badge>
            <Badge variant="light">
              {t(`leads.${lowerCaseNthLetter(processStage)}`)}
            </Badge>
          </Flex>
          <IconDots width={20} height={20} style={{ display: "none" }} />
        </Flex>

        <SimpleGrid cols={{ xs: 2, sm: 2, md: 3, lg: 6, xl: 6 }} spacing={8}>
          <Stack gap={0} justify="center">
            <Text className={classes.listItemLabel}>
              {t(`leads.callbackTime`)}
            </Text>
            <Text className={classes.listItemText}>
              {new Date(callback.startDate!).toLocaleTimeString([], {
                month: "2-digit",
                day: "2-digit",
                hour: "2-digit",
                minute: "2-digit",
                hour12: false,
              })}
            </Text>
          </Stack>
          <Stack gap={0} justify="center">
            <Text className={classes.listItemLabel}>{t(`leads.callDue`)}</Text>
            <Text className={classes.listItemText} c={isDue ? "red" : "green"}>
              {timeLeft}
            </Text>
          </Stack>
          <Stack gap={0} justify="center">
            <Text className={classes.listItemLabel}>{t(`leads.mobile`)}</Text>
            <Text className={classes.listItemText}>
              {callback.lead?.mobile ?? ""}
            </Text>
          </Stack>{" "}
          <Stack gap={0} justify="center">
            <Text className={classes.listItemLabel}>{t(`leads.email`)}</Text>
            <Text className={classes.listItemText}>
              {callback.lead?.email ?? ""}
            </Text>
          </Stack>
        </SimpleGrid>
      </Box>
    </Anchor>
  );
}
